import { BigNumber } from '@ethersproject/bignumber'
import { AVG_MINING_TIME, getCurrencyValue, getStakingAddress, StakingOption } from './utils'
import { useClaimableAmounts, useStaking } from './hooks'
import { useEthers } from '../../../hooks'
import { useBlockNumber } from '../../../providers'
import { UserStakePosition } from './StakePosition'

export function usePositions(stakingOption: StakingOption) {
  const { chainId, account } = useEthers()
  const stakingAddress = chainId && getStakingAddress(stakingOption, chainId)
  const latestBlock = useBlockNumber()
  const { durationInBlocks, stopBlock, userStakes } = useStaking(stakingAddress, account)

  const claimableAmounts = useClaimableAmounts(userStakes ? userStakes.length : 0, stakingAddress, account)
  const positions =
    chainId && latestBlock && userStakes && stopBlock !== undefined && durationInBlocks && claimableAmounts
      ? userStakes.map(({ startBlock, lockedAmount, withdrawn }, idx) => ({
          endAt: getEndingDate(startBlock, stopBlock, durationInBlocks, latestBlock),
          total: getCurrencyValue(chainId, lockedAmount),
          claimable: getCurrencyValue(chainId, claimableAmounts[idx]),
          withdrawable: getCurrencyValue(
            chainId,
            stopBlock > 0 && latestBlock > stopBlock && !withdrawn ? lockedAmount : BigNumber.from(0)
          ),
          withdrawn,
        }))
      : []
  const canStake = latestBlock && stopBlock !== undefined && (stopBlock == 0 || latestBlock < stopBlock)
  const claimable = chainId && getCurrencyValue(chainId, getClaimableSum(positions))
  const withdrawable = chainId && getCurrencyValue(chainId, getWithdrawableSum(positions))
  return {
    canStake,
    claimable,
    withdrawable,
    positions,
  }
}

function getClaimableSum(positions: UserStakePosition[]) {
  return positions.reduce((sum, position) => sum.add(position.claimable?.value || 0), BigNumber.from(0))
}

function getWithdrawableSum(positions: UserStakePosition[]) {
  return positions.reduce((sum, position) => sum.add(position.withdrawable?.value || 0), BigNumber.from(0))
}

function getEndingDate(startBlock: number, stopBlock: number, durationInBlocks: number, latestBlock: number) {
  const plannedEndBlock = startBlock + durationInBlocks
  const endBlock = stopBlock === 0 || plannedEndBlock < stopBlock ? plannedEndBlock : stopBlock
  if (latestBlock > endBlock) {
    return
  }
  const blocksToEnd = endBlock - latestBlock
  return new Date(Date.now() + blocksToEnd * AVG_MINING_TIME)
}
